import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import MaterialModal from "../MaterialModal";
import withModal from "../withModal";

import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";

import { hideModal } from "../../../actions/actionCreators";
import { getListServiceCenterAsync, addGift, editGift, editAllDates } from "../../../actions/makeAWish";

import { IAppState } from "../../../types/stores";
import { IGift } from "../../../types/stores/makeAWish";

type ModalProps = {
    type: "add" | "edit" | "date";
    gift: IGift | undefined;
};

type Props = {
    isOpen: boolean;
    modalProps: ModalProps;
};

const emptyGift: IGift = {
    Region: "",
    RunDate: "",
    StartDate: "",
    EndDate: "",
    ExportName: "",
    Email: "",
    ReportUser: "",
    Description: "",
    VersionKey: "",
    ReportName: "",
    UpperExportName: "",
};

const AddEditGiftModal: React.FC<Props> = ({ isOpen, modalProps }: Props) => {
    const dispatch = useDispatch();

    const [newGift, setNewGift] = useState<IGift>(emptyGift);
    const [isEmailsValid, setIsEmailsValid] = useState(true);

    const serviceCenters = useSelector((state: IAppState) => state.makeAWish.serviceCenters);

    useEffect(() => {
        if (modalProps.gift) {
            setNewGift(modalProps.gift);
        }
    }, [modalProps.gift]);

    useEffect(() => {
        if (isOpen) {
            dispatch(getListServiceCenterAsync());
        }
    }, [dispatch, isOpen]);

    const hide = () => {
        dispatch(hideModal("ADD_EDIT_GIFT"));
        setNewGift(emptyGift);
    };

    const save = () => {
        if (isEmailsValid && isFinished()) {
            switch (modalProps.type) {
                case "add":
                    dispatch(addGift(newGift));
                    break;
                case "edit":
                    dispatch(editGift(newGift));
                    break;
                case "date":
                    dispatch(editAllDates(newGift.RunDate, newGift.StartDate, newGift.EndDate));
                    break;
                default:
                    break;
            }
            hide();
        }
    };

    const getTitle = () => {
        let title;

        switch (modalProps.type) {
            case "add":
                title = "Add Gift";
                break;
            case "edit":
                title = "Edit Gift";
                break;
            case "date":
                title = "Edit All Dates";
                break;
            default:
                title = "";
        }

        return title;
    };

    function validateEmails(string) {
        if (string == undefined || !string.trim()) {
            return setIsEmailsValid(true);
        }

        const regex = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
        const result = string.replace(/\s/g, "").split(/,|;/);
        for (let i = 0; i < result.length; i++) {
            if (!regex.test(result[i])) {
                return setIsEmailsValid(false);
            }
        }

        return setIsEmailsValid(true);
    }

    const isFinished = () => {
        let isValid = newGift.RunDate && newGift.StartDate && newGift.EndDate;

        if (modalProps.type != "date") {
            return (
                isValid && newGift.ExportName && newGift.ReportUser && newGift.Email && newGift.Region && isEmailsValid
            );
        }

        return Boolean(isValid);
    };

    const formatDate = (date: moment.MomentInput) => (date ? moment(date).format("YYYY-MM-DD").toString() : "");

    return (
        <MaterialModal
            id={"edit-donor-modal" + modalProps.type}
            isOpen={isOpen}
            onClose={() => hide()}
            title={getTitle()}
            onPrimaryButtonRequest={() => save()}
            isPrimaryButtonDisabled={!isFinished()}
            showPrimaryButton
            primaryButtonText="Save"
            cancelButtonText="Cancel"
        >
            <div style={{ width: "500px" }}>
                {modalProps.type != "date" && (
                    <>
                        <FormControl
                            style={{
                                width: "100%",
                                flexGrow: 2,
                                fontSize: 18,
                            }}
                        >
                            <InputLabel htmlFor="Region">Region</InputLabel>
                            <Select
                                id="Title"
                                onChange={e => {
                                    const update = { ...newGift };
                                    update.Region = e.target.value as string;
                                    setNewGift(update);
                                }}
                                value={newGift.Region || ""}
                            >
                                <MenuItem key="default" value="">
                                    Select Region...
                                </MenuItem>
                                {serviceCenters.map(option => (
                                    <MenuItem key={option.Service} value={option.Service!.substring(0, 3)}>
                                        {option.Service!.substring(0, 3) + " " + option.Description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            id="report-user"
                            label="Report User"
                            type="text"
                            onChange={e => {
                                const update = { ...newGift };
                                update.ReportUser = e.target.value;
                                setNewGift(update);
                            }}
                            value={newGift.ReportUser || ""}
                            style={{ width: "100%" }}
                        />
                        <TextField
                            id="report-name"
                            label="Export Name"
                            type="text"
                            onChange={e => {
                                const update = { ...newGift };
                                update.ExportName = e.target.value;
                                setNewGift(update);
                            }}
                            value={newGift.ExportName || ""}
                            style={{ width: "100%" }}
                        />
                        <TextField
                            id="emails"
                            label="Emails"
                            error={!isEmailsValid}
                            helperText={
                                isEmailsValid
                                    ? "Please separate multiple emails with a semi-colon ';'."
                                    : "Please enter valid email address(es)."
                            }
                            type="email"
                            onChange={e => {
                                const update = { ...newGift };
                                update.Email = e.target.value;
                                validateEmails(update.Email);
                                setNewGift(update);
                            }}
                            value={newGift.Email || ""}
                            style={{ width: "100%" }}
                        />
                    </>
                )}
                <TextField
                    id="run-date"
                    label="Run Date"
                    type="date"
                    onChange={e => {
                        const update = { ...newGift };
                        update.RunDate = e.target.value;
                        setNewGift(update);
                    }}
                    value={formatDate(newGift.RunDate) || ""}
                    style={{ width: "100%" }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <TextField
                    id="start-date"
                    label="Start Date"
                    type="date"
                    onChange={e => {
                        const update = { ...newGift };
                        update.StartDate = e.target.value;
                        setNewGift(update);
                    }}
                    value={formatDate(newGift.StartDate) || ""}
                    style={{ width: "100%" }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <TextField
                    id="end-date"
                    label="End Date"
                    type="date"
                    onChange={e => {
                        const update = { ...newGift };
                        update.EndDate = e.target.value;
                        setNewGift(update);
                    }}
                    value={formatDate(newGift.EndDate) || ""}
                    style={{ width: "100%" }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </div>
        </MaterialModal>
    );
};

export default withModal(AddEditGiftModal, "ADD_EDIT_GIFT");
